import { ZLBoltOptions } from "@zerolight-core/libzl";
import { DefaultAppConfigType } from "./types/VisualizerTypes";
import { defineMessage } from "react-intl";
import { getInitialUrlArgs } from "./UrlArgsProvider";

const {assetService, timeout, forceCacheMiss, lazyLoad} = getInitialUrlArgs()

const masterModel = {
    manufacturer: "slate",
    model: "truck",
    year: "2025"
}

const maxX = 1920;
const maxY = 1080;
const minX = Math.min(Math.max(256, maxX / 4), maxX);
const minY = Math.max(256, maxY / 4);
export const disconnectDelay = 10000;

export const PreloadImageLandscape = "https://images-slatestage.omnistream.zlthunder.net/parking_lot/slate/truck/2025/a9ce2642db63e2de/c59ffa535409095266a7afc1290d2e9209df32ee_1920x1080.jpg";
export const PreloadImagePortrait = "https://images-slatestage.omnistream.zlthunder.net/parking_lot/slate/truck/2025/a9ce2642db63e2de/f9611b8667516628ae480067255db89d49166d9c_1920x1080.jpg";

const interiorXFactor = getInitialUrlArgs().interiorInvertV ? -1 : 1;
const interiorYFactor = getInitialUrlArgs().interiorInvertH ? -1 : 1;

const zlboltOptions: ZLBoltOptions = {
    connectionParameters: {
      customer: "omnistream",
      environment: assetService,
      protocol: "https"
    },
    useRegionService: true,
    clientApplication: "configurator",
    parent: "streamContainer",
    car: masterModel,
    loadingScreen: false,
    modes: ["2D"],
    useAdvancedBandwidthAssess: false,
    allow2DFallback: false,
    allow2DFallbackFatal: false,
    allow3DUpgrade: false,
    forceUpgradeViabilityCheck: true,
    allowContinuousReconnectAttempts: false,
    allow2D360View: true,
    idleOverlay: false,
    idleDisconnect: timeout && timeout > 0 ? timeout * 1000 : -1,
    idleConfig: {
        initialWindow: 0,
        extendedTimeout: timeout * 1000 - disconnectDelay,
        initialTimeout: timeout * 1000 - disconnectDelay,
    },
    keepLastFrameOnIdleDisconnect: true,
    idleDowngrade: -1,
    enableClientCreationBackoff: true,
    force2DCacheMiss: forceCacheMiss,
    onDemand2DConfig: {
        onDemand2D360ViewRules: {
            loadAuto: !lazyLoad,
            loadOnMoveCamera: lazyLoad,
            loadOnRotation: lazyLoad,
        },
        chainFrameBatches: true,
        showProgressBar: false,
        //renderQuality: "Fast",
        renderQuality: "Custom",
        renderHints: {
            customMSAA: 2,
            customSSAA: 1
        } 
    },
    mouseTracking: {
        enabled: true,
        eventFreq: 20,
        enableScrollZoom: true,
        enableMultiTouchGestures: true,
        scale: {
            interior: [40 * interiorYFactor, 85 * interiorXFactor, 10 * interiorYFactor, 40 * interiorXFactor],
            exterior: [140,140,60,60],
        }
    },
    scalingConfig: {
        showOverflow: false,
        autoScale: true,
        useExact3DOutputResolution: true,
        update3DOutputResolution: true,
        profileSwitchingRules: {
            default: "default",
        },
        qualitySettings3D: {},
        profiles: {
            default: {
                "2D": {
                    bounds: { min: [0, 0], max: [9999, 9999] },
                    permittedSizes: [
                        [1920, 1080],
                    ],
                    enableMediaScaling: true,
                    scaleMode: "fill",
                },
                "3D": {
                    bounds: { min: [0, 0], max: [9999, 9999] },
                    streamResolutionBounds: { min: [minX, minY], max: [maxX, maxY] },
                    enableMediaScaling: true,
                    aspectRatio: maxX / maxY,
                    scaleMode: "fill",
                },
            },
        },
    },
    richDataStreamConfig: {
        enable2DODSupport: true,
        initialRichData2DSourceIDs: [2, 3, 7]
    },
    frameMetadataConfig: {
        enabled: true,
    },
    recordSessionDetails: true,
    relativeMouseZoom: true,
    movableCameraBehaviour: "manual",
}

export const AppConfig: DefaultAppConfigType = {
    visualizer: {
        allowedModes: ["2D", "3D"],
        zlboltOptions: zlboltOptions,

        categories: [
            {
                id: "starter_packs",
                displayName: { id: "never", defaultMessage: " "},
                group: "starter_packs",
                nothingSelectedMessage: {id: "never", defaultMessage: " "}
            }
        ],
        sceneCategories: [
            {
                id: "environments",
                displayName: defineMessage({ id: "sceneCategory.environments", defaultMessage: "Environments" }),
                icon: "Backgrounds",
                group: "environments",
            },
            {
                id: "cameras",
                displayName: defineMessage({ id: "sceneCategory.cameras", defaultMessage: "Cameras" }),
                icon: "Cameras",
                group: "cameras"
            },
            {
                id: "hotspots",
                displayName: defineMessage({ id: "sceneCategory.animations", defaultMessage: "Animations" }),
                icon: "Animations",
                group: "hotspots",
                allowedModes: ["3D"]
            }
        ],
        configuration: {
            optionsGroups: {
                cameras: {
                    canBeEmpty: false,
                    selectionMode: "single",
                    options: {
                        FrontThreeQuarter: {
                            displayName: defineMessage({ id: "option.cameras.FrontThreeQuarter", defaultMessage: "Front Three Quarter" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Ext_FrontThreeQuarter_Landscape",
                            }
                        },
                        Side: {
                            displayName: defineMessage({ id: "option.cameras.Side", defaultMessage: "Side" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Ext_Side_Landscape",
                            }
                        },
                        RearThreeQuarter: {
                            displayName: defineMessage({ id: "option.cameras.RearThreeQuarter", defaultMessage: "Rear Three Quarter" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Ext_RearThreeQuarter_Landscape",
                            }
                        },
                        Wheel: {
                            displayName: defineMessage({ id: "option.cameras.Wheel", defaultMessage: "Wheel" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Ext_Wheel_Landscape",
                            }
                        },
                        Roof: {
                            displayName: defineMessage({ id: "option.cameras.Roof", defaultMessage: "Roof" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Ext_Roof_Landscape",
                            }
                        },
                        FrontGrille: {
                            displayName: defineMessage({ id: "option.cameras.FrontGrille", defaultMessage: "Front Grille" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Ext_FrontGrille_Landscape",
                            }
                        },
                        Driver: {
                            displayName: defineMessage({ id: "option.cameras.Driver", defaultMessage: "Driver" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Int_Driver_Landscape",
                                animationStates: { ICONS: 1 },
                                instantAnimations: { ICONS: true },
                                forceAnimationStates: { ICONS: 1 }
                            }
                        },
                        IP: {
                            displayName: defineMessage({ id: "option.cameras.IP", defaultMessage: "IP" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Int_IP_Landscape",
                                animationStates: { DoorFR: 1, ICONS: 1 },
                                instantAnimations: { DoorFR: true, ICONS: true },
                                forceAnimationStates: {
                                    DoorFR: 1,
                                    ICONS: 1
                                }
                            }
                        },
                        Slatelets: {
                            displayName: defineMessage({ id: "option.cameras.Slatelets", defaultMessage: "Slatelets" }),
                            thumbnail: { type: "image", url: "" },
                            disabled: true,
                            hidden: true,
                            visualizerState: {
                                camera: "Int_Slatelets_Landscape",
                                animationStates: { DoorFR: 1 },
                                instantAnimations: { DoorFR: true },
                                forceAnimationStates: {
                                    DoorFR: 1
                                }
                            }
                        },
                        FrontSeats: {
                            displayName: defineMessage({ id: "option.cameras.FrontSeats", defaultMessage: "Front Seats" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Int_FrontSeats_Landscape",
                            }
                        },
                        DoorsWindows: {
                            displayName: defineMessage({ id: "option.cameras.DoorsWindows", defaultMessage: "Doors & Windows" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Int_DoorsWindows_Landscape",
                            }
                        },
                        AudioTech: {
                            displayName: defineMessage({ id: "option.cameras.AudioTech", defaultMessage: "Audio & Tech" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Int_AudioAndTech_Landscape",
                                animationStates: { ICONS: 1 },
                                instantAnimations: { ICONS: true },
                                forceAnimationStates: { ICONS: 1 }
                            }
                        },
                        FloorMatsAndPedals: {
                            displayName: defineMessage({ id: "option.cameras.FloorMatsAndPedals", defaultMessage: "Floor Mats & Pedals" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Int_FloorMatsandPedals_Landscape"
                            }
                        },
                        RearSeats: {
                            displayName: defineMessage({ id: "option.cameras.RearSeats", defaultMessage: "Rear Seats" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                camera: "Int_RearSeats_Landscape_Static",
                            }
                        },
                    },
                },
                animations: {
                    canBeEmpty: true,
                    selectionMode: "multiple",
                    options: {
                        "BIN": {
                            displayName: defineMessage({id: "animation.bin.displayName", defaultMessage: "Bin"}),
                            thumbnail: { type: "image", url: ""},
                            visualizerState: {
                                animationStates: {
                                    "BIN": 1,
                                }
                            },
                        },
                        "DRL": {
                            displayName: defineMessage({id: "animation.drl.displayName", defaultMessage: "DRL"}),
                            thumbnail: { type: "image", url: ""},
                            visualizerState: {
                                animationStates: {
                                    "DRL": 1,
                                }
                            },
                        },
                        "DoorFL": {
                            displayName: defineMessage({id: "animation.door_fl.displayName", defaultMessage: "DoorFL"}),
                            thumbnail: { type: "image", url: ""},
                            visualizerState: {
                                animationStates: {
                                    "DoorFL": 1,
                                }
                            },
                        },
                        "DoorFR": {
                            displayName: defineMessage({id: "animation.door_fr.displayName", defaultMessage: "DoorFR"}),
                            thumbnail: { type: "image", url: ""},
                            visualizerState: {
                                animationStates: {
                                    "DoorFR": 1,
                                }
                            },
                        },
                        "DoorsF": {
                            displayName: defineMessage({id: "animation.doors_f.displayName", defaultMessage: "DoorsF"}),
                            thumbnail: { type: "image", url: ""},
                            visualizerState: {
                                animationStates: {
                                    "DoorsF": 1,
                                }
                            },
                        },
                        "Frunk": {
                            displayName: defineMessage({id: "animation.frunk.displayName", defaultMessage: "Frunk"}),
                            thumbnail: { type: "image", url: ""},
                            visualizerState: {
                                animationStates: {
                                    "Frunk": 1,
                                }
                            },
                        },
                        "Headlights": {
                            displayName: defineMessage({id: "animation.headlights.displayName", defaultMessage: "Headlights"}),
                            thumbnail: { type: "image", url: ""},
                            visualizerState: {
                                animationStates: {
                                    "Headlights": 1,
                                }
                            },
                        },
                        "Trunk": {
                            displayName: defineMessage({id: "animation.trunk.displayName", defaultMessage: "Trunk"}),
                            thumbnail: { type: "image", url: ""},
                            visualizerState: {
                                animationStates: {
                                    "Trunk": 1,
                                }
                            },
                        },
                    }
                },
                environments: {
                    canBeEmpty: false,
                    selectionMode: "single",
                    options: {
                        /*
                        void: {
                            displayName: defineMessage({ id: "option.environments.void", defaultMessage: "Void" }),
                            thumbnail: { type: "image", url: "" },
                            
                            visualizerState: {
                                environment: "RTTVoid",
                            }
                        },
                        */
                        parking_lot_day: {
                            displayName: defineMessage({ id: "option.environments.parking_lot_day", defaultMessage: "Day" }),
                            disabled: false,
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                environment: "parking_lot"
                            }
                        },
                        parking_lot_night: {
                            displayName: defineMessage({ id: "option.environments.parking_lot_night", defaultMessage: "Night" }),
                            disabled: false,
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {
                                environment: "parking_lot_night",
                                animationStates: { Headlights: 1, DRL: 1, SideLights: 1, FogLights: 1},
                                instantAnimations: { Headlights: true, DRL: true, SideLights: true, FogLights: true },
                                forceAnimationStates: { Headlights: 1, DRL: 1, SideLights: 1, FogLights: 1 }
                            }
                        },
                        slate_colour_studio: {
                            thumbnail: { type: "image", url: "" },
                            displayName: defineMessage({ id: "option.environments.slate_colour_studio", defaultMessage: "Color Dip" }),
                            disabled: false,
                            visualizerState: {
                                environment: "slate_colour_studio",
                            }
                        }
                    }
                },
                hotspots: {
                    canBeEmpty: false,
                    selectionMode: "single",
                    options: {
                        off: {
                            displayName: defineMessage({ id: "option.hotspots.off", defaultMessage: "Hide Hotspots" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {}
                        },
                        on: {
                            displayName: defineMessage({ id: "option.hotspots.on", defaultMessage: "Show Hotspots" }),
                            thumbnail: { type: "image", url: "" },
                            visualizerState: {}
                        }
                    }
                },
                starter_packs: {
                    canBeEmpty: true,
                    selectionMode: "single",
                    options: {},
                    // options: {
                    //     pack_0: {
                    //         displayName: defineMessage({ id: "option.starter_pack.pack_0", defaultMessage: "Starter pack 0" }),
                    //         thumbnail: { type: "image", url: "./mock-rect.png" },
                    //         visualizerState: {
                    //         },
                    //         buildSessionUpdate: {
                    //             manufacturer: "slate",
                    //             model: "truck",
                    //             year: "2025",
                    //             pkg: "truck",
                    //             desiredOptions: ["1", "3", "8"]
                    //         }
                    //     },
                    //     pack_1: {
                    //         displayName: defineMessage({ id: "option.starter_pack.pack_1", defaultMessage: "Starter pack 1" }),
                    //         thumbnail: { type: "image", url: "./mock-rect2.png" },
                    //         visualizerState: {
                    //         },
                    //         buildSessionUpdate: {
                    //             manufacturer: "slate",
                    //             model: "truck",
                    //             year: "2025",
                    //             pkg: "truck",
                    //             desiredOptions: ["5", "10", "7"]
                    //         }
                    //     },
                    //     pack_2: {
                    //         displayName: defineMessage({ id: "option.starter_pack.pack_2", defaultMessage: "Starter pack 2" }),
                    //         thumbnail: { type: "image", url: "./mock-rect3.png" },
                    //         visualizerState: {
                    //         },
                    //         buildSessionUpdate: {
                    //             manufacturer: "slate",
                    //             model: "truck",
                    //             year: "2025",
                    //             pkg: "truck",
                    //             desiredOptions: ["6", "12", "30"]
                    //         }
                    //     }
                    // }
                }
            },
            selectedOptions: {
                starter_packs: [],
                cameras: ["FrontThreeQuarter"],
                environments: ["parking_lot"],
                animations: [],
                hotspots: ["off"]
            },
            customWraps: {},
            appliedWraps: {}
        },
        initialState: {
            animationStates: {
                "DRL": 0,
                "DoorFL": 0,
                "DoorFR": 0,
                "Frunk": 0,
                "Headlights": 0,
                "Trunk": 0,
                "BIN": 0,
                "ICONS": 0,
                "SideLights": 0,
                "FogLights": 0
            },
            camera: "Ext_FrontThreeQuarter_Landscape",
            environment: "parking_lot",
            prcodes: ["VERSION_1"],
            instantAnimations: {},
        },
        //movableCamera: "Ext_FrontThreeQuarter_Landscape",
        cameraSwitchOptions: [
            {
                cameras: [
                    "Ext_RearDetail_Landscape",
                    "Ext_RearDetail_Portrait",
                    "Ext_RearThreeQuarter_Landscape",
                    "Ext_RearThreeQuarter_Portrait",
                    "Ext_Side_Landscape",
                    "Ext_Side_Portrait",
                    "Ext_Wheel_Landscape",
                    "Ext_Wheel_Portrait",
                    "Ext_FrontThreeQuarter_Landscape",
                    "Ext_FrontThreeQuarter_Portrait",
                    "Ext_FrontGrille_Landscape",
                    "Ext_FrontGrille_Portrait",
                    "Ext_Roof_Landscape",
                    "Ext_Roof_Portrait",
                ],
                switchOptions: { smooth: true, movementMode: "flysmooth" },
            },
            {
                cameras: [
                    // "Int_360_Landscape",
                    // "Int_360_Portrait",
                    // "Int_Driver_Landscape",
                    // "Int_Driver_Portrait",
                    // "Int_SteeringWheel_Landscape",
                    // "Int_SteeringWheel_Portrait",
                    // "Int_FrontSeats_Landscape",
                    // "Int_FrontSeats_Portrait",
                    // "Int_IP_Landscape",
                    // "Int_IP_Portrait",
                    // "Int_RearSeats_Landscape_Static",
                    // "Int_RearSeats_Portrait_Static",
                ],
                switchOptions: { smooth: true, movementMode: "flysmoothdirect" },
            }
        ],
        mediaCameraRemap: {
            "(orientation:portrait)": {
                Ext_Orbit_Landscape: "Ext_Orbit_Portrait",
                Ext_RearDetail_Landscape: "Ext_RearDetail_Portrait",
                Ext_RearThreeQuarter_Landscape: "Ext_RearThreeQuarter_Portrait",
                Ext_Side_Landscape: "Ext_Side_Portrait",
                Ext_Wheel_Landscape: "Ext_Wheel_Portrait",
                Ext_FrontThreeQuarter_Landscape: "Ext_FrontThreeQuarter_Portrait",
                Ext_FrontGrille_Landscape: "Ext_FrontGrille_Portrait",
                Ext_Roof_Landscape: "Ext_Roof_Portrait",
                Int_360_Landscape: "Int_360_Portrait",
                Int_Driver_Landscape: "Int_Driver_Portrait",
                Int_SteeringWheel_Landscape: "Int_SteeringWheel_Portrait",
                Int_FrontSeats_Landscape: "Int_FrontSeats_Portrait",
                Int_IP_Landscape: "Int_IP_Portrait",
                Int_RearSeats_Landscape_Static: "Int_RearSeats_Portrait_Static",
                Int_Slatelets_Landscape: "Int_Slatelets_Portrait",
                Int_DoorsWindows_Landscape: "Int_DoorsWindows_Portrait",
                Int_AudioAndTech_Landscape: "Int_AudioAndTech_Portrait",
                Int_FloorMatsandPedals_Landscape: "Int_FloorMatsandPedals_Portrait"
            }
        },
        //camera2DConfig: Camera2DConfig,
    }
}

export const proxyTemplate = (prcodes: string, parts: string) => ({
    car: "slate truck 2025",
    prcodes: prcodes,
    texture_format: "png",
    type: "carProxyConfig",
    export_format: "glb",
    disksize: 20,
    version: "2",
    "camera": {
        "data": {
            "Advanced": {
                "ZLCertifiedEffectsValues": {
                    "ZLCert_BespokeAR": {
                        "Dynamic Component Types": {
                            "exportSettings": "ZLMODProxyTypes.ProxyExportSettings"
                        },
                        "estFileSizeStr": "25.96MB",
                        "exportSettings__autoDistributeTris": true,
                        "exportSettings__doWheelsSeparate": false,
                        "exportSettings__exteriorTransparencyEnabled": false,
                        "exportSettings__exteriorUsesSeparateTextureSettings": false,
                        "exportSettings__generateInterior": false,
                        "exportSettings__materialLayerMode": "Clear Coat Masked",
                        "exportSettings__maxTriangleCount": 200000,
                        "exportSettings__proxyExportScale": 0.1,
                        "exportSettings__ssaa": 1,
                        "exportSettings__textureCompressionLevel": 75,
                        "exportSettings__textureFormat": "Jpeg",
                        "exportSettings__textureSizeDim": 4096,
                        "m_SelectedPreset": "Default"
                    },
                    "ZLCert_PartHighlighting": {
                        "Apply": false,
                        "CenterPart": true,
                        "PRCodes": parts,
                        "PartColor": [
                            1,
                            1,
                            1,
                            1
                        ],
                        "PartEffect": "None",
                        "SerializedApply": true,
                        "VehicleColor": [
                            1,
                            1,
                            1,
                            1
                        ],
                        "VehicleEffect": "Hide"
                    }
                }
            },
            "CamData": {
                "FOV": 25,
                "FarPlane": 10000,
                "NearPlane": 0.15,
                "Position": {
                    "x": -3.840622,
                    "y": 1.204341,
                    "z": 6.556425
                },
                "Rotation": {
                    "x": 4,
                    "y": 150,
                    "z": 0
                }
            },
            "CameraName": "2DOD",
            "Flags": {
                "raytraced_reflections": null
            }
        },
        "displayName": "GlobalStageCamera",
        "name": "GlobalStageCamera",
        "specification": "jsoncamera"
    },
})